import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation as useRouterLocation } from 'react-router';

import { AppRouteKeys, getRoutePath } from '~/core/router/router_utils';
import { AppRoutes } from '~/core/router/routes';
import { useNavigationStore } from '~/core/stores/navigation_store';
import { useNavigation } from '~/core/utils/navigation';
import { useLayoutStore } from '~/shared/stores/layoutStore';

type SecureLinkProps = {
  to: AppRouteKeys;
  params?: Record<string, string>;
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
};

/**
 * A simplified secure link component that relies on server-side validation
 * The middleware will handle all permission checks
 */
export function SecureLink({ to, params, className, children, onClick, href }: SecureLinkProps) {
  const { goTo } = useNavigation();
  const navigate = useNavigate();
  const location = useRouterLocation();
  const setPendingNavigation = useLayoutStore((state) => state.setPendingNavigation);
  const [isValidating, setIsValidating] = useState(false);

  // Clear isPendingNavigation when the route actually changes
  useEffect(() => {
    setPendingNavigation(false);
  }, [location.key]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (isValidating) return;
    setIsValidating(true);
    setPendingNavigation(true);

    const { currentRoute } = useNavigationStore.getState();
    console.info(
      `🔗 [SecureLink] Validating navigation from ${currentRoute || 'UNKNOWN'} to ${to}`
    );

    const targetPath = href || (params ? getRoutePath(to, params) : AppRoutes[to].path);

    const headers = new Headers();
    headers.set('X-Client-Navigation', 'true');
    headers.set('X-Client-Nav-From', currentRoute || 'UNKNOWN');
    headers.set('X-Transition-Check', 'true');

    fetch(targetPath, {
      method: 'HEAD',
      headers,
    })
      .then((response) => {
        if (response.ok) {
          console.info(`✅ [SecureLink] Navigation allowed to ${to}`);

          if (onClick) {
            onClick(e);
          }

          // Navigate — isPendingNavigation stays true until route changes
          if (params || href) {
            navigate(targetPath);
          } else {
            goTo(to);
          }
        } else {
          console.info(`❌ [SecureLink] Navigation denied to ${to}`);
          setPendingNavigation(false);
          navigate(AppRoutes.NO_ACCESS.path);
        }
      })
      .catch((error) => {
        console.error('Error during transition validation:', error);
        setPendingNavigation(false);
        navigate(AppRoutes.NO_ACCESS.path);
      })
      .finally(() => {
        setIsValidating(false);
        // NOTE: Do NOT clear setPendingNavigation here on success.
        // It gets cleared by the useEffect when location.pathname changes.
      });
  };

  const targetPath = href || (params ? getRoutePath(to, params) : AppRoutes[to].path);

  return (
    <Link
      to={targetPath}
      className={className}
      onClick={handleClick}
      data-testid={`secure-link-${to}`}
      data-route-key={to}
      preventScrollReset={false}
    >
      {children}
    </Link>
  );
}
